import axios from 'axios'
import { colors } from 'fitify-ui'
import { ContentRevealAnimation } from 'fitify-ui-landing/src/components/animations/ContentRevealAnimation'
import Section from 'fitify-ui-landing/src/components/Section/Section'
import { Text } from 'fitify-ui-landing/src/components/Text'
import { EMAIL_REGEXP } from 'fitify-utils/src/validations'
import { useTranslation } from 'next-i18next'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'

import Container from '@/components/Container/Container'

import {
  ButtonLoader,
  StyledNewsletter,
  StyledNewsletterButton,
  StyledNewsletterContainer,
  StyledNewsletterCopy,
  StyledNewsletterEmailInput,
  StyledNewsletterEmailSuccessMessage,
  StyledNewsletterErrorMessage,
  StyledNewsletterForm,
  StyledNewsletterFormContainer,
} from './Newsletter.Styled'

type NewsletterFormInputs = {
  email: string
}

const NEXT_PUBLIC_LIST_ID = process.env.NEXT_PUBLIC_LIST_ID

interface NewsletterProps {
  blockText: string
  emailPlaceholderText: string
  submitButtonText: string
  successMessage: string
  errorMessage: string
}

const Newsletter = ({
  blockText,
  emailPlaceholderText,
  submitButtonText,
  successMessage,
  errorMessage,
}: NewsletterProps) => {
  const { t } = useTranslation()
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    formState: { errors, isSubmitSuccessful },
  } = useForm<NewsletterFormInputs>()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isFaded, setIsFaded] = useState<boolean>(false)
  const timer = useRef<NodeJS.Timeout | undefined>(undefined)

  useEffect(() => {
    if (!isSubmitSuccessful) {
      setIsFaded(false)
      return
    }

    if (!isLoading) setIsFaded(true)

    timer.current = setTimeout(() => {
      setIsFaded(false)
    }, 5000)

    return () => {
      clearTimeout(timer.current)
    }
  }, [isSubmitSuccessful, isLoading])

  const onSubmit = handleSubmit(async (data) => {
    setIsLoading(true)
    try {
      const response = await axios.post(
        `/api/newsletter`,
        {
          list_id: NEXT_PUBLIC_LIST_ID,
          email: data.email,
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      )

      if (response.status === 200) {
        setValue('email', '')
      }

      setIsLoading(false)
    } catch {
      setIsLoading(false)
      setError('email', {
        type: 'server',
        message: t('cl_email_sign_up_invalid_credentials_message'),
      })
    }
  })

  return (
    <Section
      style={{
        background: colors.Mantis300,
        padding: 0,
        marginBottom: '-46px',
        borderRadius: '40px 40px 0 0',
      }}
    >
      <Container>
        <StyledNewsletterContainer id={'newsletter'}>
          <ContentRevealAnimation>
            <StyledNewsletter>
              <StyledNewsletterCopy>
                <Text
                  as={'p'}
                  $variant={'h3'}
                  color={'Gray600'}
                  variant={'newsletter-title'}
                  html={t(blockText)}
                />
              </StyledNewsletterCopy>
              <StyledNewsletterFormContainer>
                <StyledNewsletterForm
                  onSubmit={(e) => void onSubmit(e)}
                  $isSuccess={isSubmitSuccessful}
                  $hasError={!!errors.email}
                >
                  <StyledNewsletterEmailInput
                    type="text"
                    placeholder={t(emailPlaceholderText)}
                    {...register('email', {
                      required: true,
                      pattern: EMAIL_REGEXP,
                    })}
                    $isSuccess={isFaded}
                    disabled={isFaded}
                  />
                  {isFaded ? (
                    <StyledNewsletterEmailSuccessMessage $isSuccess={isFaded}>
                      {t(successMessage)}
                    </StyledNewsletterEmailSuccessMessage>
                  ) : null}
                  {isLoading ? (
                    <ButtonLoader />
                  ) : (
                    <StyledNewsletterButton type="submit" $isSuccess={isFaded}>
                      {t(submitButtonText)}
                    </StyledNewsletterButton>
                  )}
                </StyledNewsletterForm>
                {errors.email && (
                  <StyledNewsletterErrorMessage>
                    {errors.email.message || t(errorMessage)}
                  </StyledNewsletterErrorMessage>
                )}
              </StyledNewsletterFormContainer>
            </StyledNewsletter>
          </ContentRevealAnimation>
        </StyledNewsletterContainer>
      </Container>
    </Section>
  )
}

export default Newsletter
